import React, { useEffect, useState } from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip } from 'components/mui';
import { CopyIcon, ExpandLess, ExpandMore } from 'components/icons';
import { CANtrackComment } from '../../types/comment';
import { Chip, Typography } from '@mui/material';
import CommentsTableRowExpanded from './CommentsTableRowExpanded';
import { TimeEntryEditableCell } from './inlineEditableCells/TimeEntryEditableCell';
import { TimeSpentEditableCell } from './inlineEditableCells/TimeSpentEditableCell';

interface CommentsTableRowProps {
	comment: CANtrackComment;
	decimalFormat: boolean;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
	onCopyCommentClick: (commentToCopy: CANtrackComment) => void;
}

// Adding an additional prop `caseUrl` for navigation.
const ReadMore: React.FC<{
	text: string;
	caseUrlV3: string;
	caseUrlV1: string;
}> = ({ text, caseUrlV1, caseUrlV3 }) => {
	const handleClick = (event: any) => {
		if (event.shiftKey && event.ctrlKey) {
			window.open(caseUrlV1, '_blank'); // open V1 URL if shift key is pressed while clicking
		} else {
			window.open(caseUrlV3, '_blank');
		}
	};

	return (
		<Box display="flex">
			<Tooltip title={text} placement="top" arrow>
				<Typography
					sx={{
						display: '-webkit-box',
						WebkitLineClamp: 1,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						cursor: 'pointer',
						textDecoration: 'none',
						'&:hover': {
							textDecoration: 'underline',
						},
					}}
					onClick={handleClick}
				>
					{text}
				</Typography>
			</Tooltip>
		</Box>
	);
};

const CommentsTableRow: React.FC<CommentsTableRowProps> = (props) => {
	const { comment, decimalFormat: showDecimalFormat, setComments } = props;
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const handleRowClick = (e: React.MouseEvent) => {
		// Only toggle on mobile, and avoid toggling when clicking buttons/inputs
		const target = e.target as HTMLElement;
		if (target.closest('button') || target.closest('input')) return;
		setIsExpanded(!isExpanded);
	};

	return (
		<>
			<TableRow
				key={comment.id}
				hover
				sx={{
					'&:last-child td, &:last-child th': {
						border: 0,
					},
					cursor: 'pointer',
				}}
				onClick={handleRowClick}
			>
				<TableCell sx={{ width: '10px', display: { xs: 'none', md: 'table-cell' } }} align="left">
					<IconButton onClick={() => setIsExpanded(!isExpanded)}>
						{isExpanded ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				</TableCell>
				<TableCell
					sx={{ width: '10px', pl: '0px', display: { xs: 'none', md: 'table-cell' } }}
					component="th"
					scope="row"
				>
					<Chip label={comment.customerName} variant="outlined" />
				</TableCell>
				<TableCell sx={{ width: '300px', display: { xs: 'none', md: 'table-cell' } }} component="th" scope="row">
					<ReadMore
						text={comment.caseTitle}
						caseUrlV3={`/cantrackv3/cases/${comment.caseID}`}
						caseUrlV1={`/Portal/Project/EditCase.aspx?tabid=9&caseid=${comment.caseID}`} // hardcoded for sds instance
					/>
				</TableCell>
				<TableCell align="left">
					<Box sx={{ minWidth: { xs: 'unset', md: '300px' } }}>{comment.title}</Box>
				</TableCell>
				<TimeEntryEditableCell
					comment={comment}
					setComments={setComments}
					sx={{ display: { xs: 'none', md: 'table-cell' } }}
				/>
				<TimeSpentEditableCell
					comment={comment}
					setComments={setComments}
					showDecimalFormat={showDecimalFormat}
				/>
				<TableCell sx={{ width: '10px', display: {md: 'table-cell' } }} align="right">
					<Tooltip title="Copy Comment">
						<IconButton
							onClick={() => props.onCopyCommentClick(comment)}
						>
							<CopyIcon />
						</IconButton>
					</Tooltip>
				</TableCell>
			</TableRow>
			{isExpanded && (
				<CommentsTableRowExpanded
					comment={comment}
					isEditing={isEditing}
					setIsEditing={setIsEditing}
					setComments={setComments}
				/>
			)}
		</>
	);
};

export default CommentsTableRow;
